import { useMutation, useQuery } from "@apollo/client";
import { getSiteIncidentReportDetail } from "../../util/query/siteIncident.query";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import Spinner from "../../components/spinner";
import { DatePicker, Dropdown, Input, Menu, Select, Skeleton } from "antd";
import "./incidentReporting.css";
import { updateSiteIncidentStatusMutation } from "../../util/mutation/siteIncidentStatus.mutation";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import TextArea from "antd/es/input/TextArea";
import { CaretDownFilled, EditOutlined, FilePdfOutlined, PrinterOutlined, ShareAltOutlined, SaveOutlined } from "@ant-design/icons";
import { usePDFGenerator } from "../complainRegisterPage/pdfGenerator.hook";
import { ReportShare } from "../../components/reportShare/reportShare";
import { shareComplaintReportEmailMutation } from "../../util/mutation/complaintRegister.mutation";
import { useDispatch } from "react-redux";
import { setNotification } from "../../middleware/redux/reducers/notification.reducer";
import { updateSiteIncidentMutation } from "../../util/mutation/siteIncident.mutation";

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

// Incident record, laid out like a service-desk ticket in three columns (the same
// shape as the app's other detail pages): key facts on the left (Details, Site),
// the officer's report in the middle, and a vertical status timeline on the right
// (every stage from New to Closed, with when it was reached and the note left
// there, plus the update composer). Colours are the app's own (see
// incidentReporting.css).

const incidentTypeOption = [
    { label: 'Theft', value: 'Theft' },
    { label: 'Damage', value: 'Damage' },
    { label: 'Assault', value: 'Assault' },
    { label: 'Trespass', value: 'Trespass' },
    { label: 'Abuse', value: 'Abuse' },
];

const incidentOutcomeOption = [
    { label: 'Deter', value: 'Deter' },
    { label: 'Arrest', value: 'Arrest' },
    { label: 'Escape', value: 'Escape' },
    { label: 'Warning', value: 'Warning' },
];

const yesNoOption = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
];

// Every metadata key Workforce360's incident form writes, in edit-form order;
// keys not listed still show/edit as plain text (humanizeKey), under "Other details".
const FIELDS = {
    incidentType:    { label: 'Incident type', type: 'dropdown', options: incidentTypeOption },
    incidentOutcome: { label: 'Outcome', type: 'dropdown', options: incidentOutcomeOption },
    date:            { label: 'Date of incident', type: 'date' },
    time:            { label: 'Time of incident', type: 'time' },
    address:         { label: 'Location', type: 'text' },
    officerName:     { label: "Security officer's name", type: 'text' },
    assignment:      { label: 'Assignment', type: 'multiline' },
    incidentDetails: { label: 'What happened', type: 'multiline' },
    immediateAction: { label: 'Immediate action taken', type: 'multiline' },
    policeAttended:  { label: 'Police attended', type: 'dropdown', options: yesNoOption },
    policeDetails:   { label: 'Police details (time, PC no., station)', type: 'multiline' },
    cctv:            { label: 'CCTV footage available', type: 'dropdown', options: yesNoOption },
    othersInvolved:  { label: 'Others involved', type: 'multiline' },
    witnesses:       { label: 'Witnesses', type: 'multiline' },
    signature:       { label: 'Signed by', type: 'text' },
};

// Theme-derived status palette - the app's teals for the normal flow, the brand
// orange for "needs attention", green/red only for the two final outcomes.
const statuses = [
    { value: "new",         label: "New",           color: "#0091ae" },
    { value: "in-review",   label: "Under review",  color: "#e8590c" },
    { value: "in-progress", label: "In progress",   color: "#126e7d" },
    { value: "on-hold",     label: "On hold",       color: "#7c98b6" },
    { value: "resolved",    label: "Resolved",      color: "#16a34a" },
    { value: "closed",      label: "Closed",        color: "#0d5c63" },
    { value: "rejected",    label: "Rejected",      color: "#dc2626" },
];
const getStatus = (value) => statuses.find((s) => s.value === value) || { value, label: value || "New", color: "#7c98b6" };

// the timeline's main path - on hold / rejected sit off it and are slotted in
// wherever they happened in time
const STEPS = ["new", "in-review", "in-progress", "resolved", "closed"];
const OFF_PATH = ["on-hold", "rejected"];

const humanizeKey = (key) =>
    FIELDS[key]?.label
    || String(key).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase());

// "N/A", "none", "-" etc. read as "nothing reported" rather than as content
const isBlankAnswer = (value) =>
    value === undefined || value === null || /^\s*(n\/?a|none|nil|nothing|-+|\.)?\s*$/i.test(String(value));

const formatOccurred = (date, time) => {
    const parsed = dayjs(`${date} ${time || "00:00"}`, "DD/MM/YYYY HH:mm", true);
    if (!date || !parsed.isValid()) return [date, time].filter(Boolean).join(" ") || "—";
    return time ? parsed.format("DD/MM/YYYY HH:mm") : parsed.format("DD/MM/YYYY");
};

const initials = (name) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const StatusPill = ({ status }) => {
    const s = getStatus(status);
    return <span className="ir-pill" style={{ color: s.color, borderColor: s.color }}>{s.label}</span>;
};

const YesNo = ({ value }) => {
    if (isBlankAnswer(value)) return <span className="ir-muted">Not stated</span>;
    const yes = String(value).trim().toLowerCase() === "yes";
    return <span className={yes ? "ir-flag ir-flag-yes" : "ir-flag"}>{yes ? "Yes" : String(value).trim().toLowerCase() === "no" ? "No" : value}</span>;
};

// "No", "No actions", "No action was taken/required" - an explicit "nothing was
// done", on top of isBlankAnswer's none/nothing/n/a
const NO_ACTION = /^\s*no(\s+(immediate\s+)?actions?(\s+(was|were))?(\s+(taken|required|needed|necessary))?)?\s*[.!]?\s*$/i;

// Answer to the mobile form's "Was any immediate action taken?" - free text, so
// anything that isn't a plain no/none reads as action taken, with the officer's
// own words underneath (a bare "Yes" has nothing more to show)
const ImmediateAction = ({ value }) => {
    const text = value == null ? "" : String(typeof value === "object" ? JSON.stringify(value) : value).trim();
    const taken = !isBlankAnswer(text) && !NO_ACTION.test(text);
    const detail = taken && !/^yes\.?$/i.test(text) ? text : null;
    return (
        <div className={`ir-narrative ir-narrative-secondary ir-action ${taken ? "ir-action-taken" : "ir-action-none"}`}>
            <div className="ir-action-title">
                {taken ? "Action taken" : text ? "No immediate action taken" : "Not stated"}
            </div>
            {detail && <div className="ir-action-text">{detail}</div>}
        </div>
    );
};

const Text = ({ value, empty = "None reported" }) =>
    isBlankAnswer(value) ? <span className="ir-muted">{empty}</span> : <>{typeof value === "object" ? JSON.stringify(value) : String(value)}</>;

const Card = ({ title, extra, children, className = "" }) => (
    <section className={`ir-card ${className}`}>
        <div className="ir-card-head">
            <h3>{title}</h3>
            {extra}
        </div>
        <div className="ir-card-body">{children}</div>
    </section>
);

// Every stage in workflow order, each marked done / current / pending / skipped,
// with on hold / rejected inserted after whichever stage came before them in time.
const buildStages = (timeline) => {
    const chrono = [...timeline].reverse(); // oldest first
    const latest = timeline[0]?.status || "new";
    const eventFor = (status) => chrono.find((t) => t?.status === status);
    let lastReached = 0;
    STEPS.forEach((s, i) => { if (eventFor(s)) lastReached = i; });
    const rejected = !!eventFor("rejected");

    const stages = [];
    STEPS.forEach((status, i) => {
        const event = eventFor(status);
        let state;
        if (event) state = status === latest && status !== "closed" ? "current" : "done";
        else if (i < lastReached) state = "skipped";
        else if (rejected) return; // process ended - nothing still to come
        else state = "pending";
        stages.push({ status, event, state });
    });

    chrono.filter((t) => OFF_PATH.includes(t?.status)).forEach((event) => {
        const at = dayjs(event.createdAt).valueOf();
        // after the last stage that was reached no later than this event
        let insertAt = 0;
        stages.forEach((st, idx) => { if (st.event && dayjs(st.event.createdAt).valueOf() <= at) insertAt = idx + 1; });
        const state = event.status === "rejected" ? "rejected" : event.status === latest ? "current" : "done";
        stages.splice(insertAt, 0, { status: event.status, event, state });
    });

    return stages;
};

const StageMarker = ({ state, index }) => (
    <span className="ir-vt-marker">
        {state === "done" ? "✓" : state === "rejected" ? "✕" : state === "skipped" ? "–" : index}
    </span>
);

const IncidentTimeline = ({ timeline, reporter }) => {
    const stages = buildStages(timeline);
    let step = 0;
    return (
        <ol className="ir-vt">
            {stages.map((stage) => {
                const s = getStatus(stage.status);
                const onPath = STEPS.includes(stage.status);
                if (onPath) step += 1;
                const event = stage.event;
                return (
                    <li key={stage.status} className={`ir-vt-item ir-vt-${stage.state}`} style={{ "--stage-color": s.color }}>
                        <StageMarker state={stage.state} index={onPath ? step : "!"} />
                        <div className="ir-vt-body">
                            <div className="ir-vt-head">
                                <span className="ir-vt-label">{s.label}</span>
                                <span className="ir-vt-time">
                                    {event
                                        ? dayjs(event.createdAt).format("DD/MM/YYYY HH:mm")
                                        : stage.state === "skipped" ? "Skipped" : "Pending"}
                                </span>
                            </div>
                            {stage.status === "new" && event && reporter && (
                                <div className="ir-vt-sub">Reported by {reporter}</div>
                            )}
                            {event && <div className="ir-vt-sub">{dayjs(event.createdAt).fromNow()}</div>}
                            {event?.comment?.trim() && <div className="ir-vt-note">{event.comment}</div>}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
};

export const IncidentReportingDetailPage = ()=>{

    const param = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const composerRef = useRef(null);

    const {data, loading, refetch} = useQuery(getSiteIncidentReportDetail,{
        variables: {
            id: param?.incidentId
        }
    });

    const [siteIncident, setSiteIncident] = useState({});

    // State for tracking changes
    const [originalValues, setOriginalValues] = useState({});
    const [currentValues, setCurrentValues] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [changedFields, setChangedFields] = useState(new Set());

    useEffect(()=>{
        if(data?.getSiteIncidentDetail?.response){
            setSiteIncident(data?.getSiteIncidentDetail?.response);

            // Initialize original values when data loads
            const metadata = data?.getSiteIncidentDetail?.response?.metadata || {};
            setOriginalValues(metadata);
            setCurrentValues(metadata);
        }else{
            setSiteIncident({});
            setOriginalValues({});
            setCurrentValues({});
        }
    },[data?.getSiteIncidentDetail?.response]);

    // Function to handle field changes
    const handleFieldChange = (fieldKey, newValue) => {
        const updatedValues = { ...currentValues, [fieldKey]: newValue };
        setCurrentValues(updatedValues);

        // Check if the new value is different from original
        const isChanged = originalValues[fieldKey] !== newValue;
        const newChangedFields = new Set(changedFields);

        if (isChanged) {
            newChangedFields.add(fieldKey);
        } else {
            newChangedFields.delete(fieldKey);
        }

        setChangedFields(newChangedFields);
        setHasChanges(newChangedFields.size > 0);
    };

    // Function to get changed values only
    const getChangedValues = () => {
        const changes = {};
        changedFields.forEach(fieldKey => {
            changes[fieldKey] = currentValues[fieldKey];
        });
        return changes;
    };

    // Function to reset all changes
    const resetChanges = () => {
        setCurrentValues(originalValues);
        setChangedFields(new Set());
        setHasChanges(false);
    };

    const [updateSiteIncidentStatus, {loading: updateSiteIncidentStatusLoading}] = useMutation(updateSiteIncidentStatusMutation);

    const [currentStatus, setCurrentStatus] = useState(undefined);
    const [comment, setComment] = useState("");

    const handelStatusUpdate = async ()=>{
        try{
            await updateSiteIncidentStatus({
                variables:{
                    input:{siteIncidentId: param?.incidentId,
                    status: currentStatus,
                    comment,}
                }
            });
            setComment("");
            setCurrentStatus(undefined);
            await refetch();
            dispatch(setNotification({
                error: false,
                notificationState: true,
                message: "Incident status updated"
            }));
        }catch(err){
            console.error(err);
        }
    };

    const [edit, setEdit] = useState(false);

    // Function to handle edit toggle
    const handleEditToggle = () => {
        if (edit && hasChanges) {
            // If exiting edit mode with changes, ask for confirmation
            if (window.confirm('You have unsaved changes. Do you want to discard them?')) {
                resetChanges();
                setEdit(false);
            }
        } else {
            setEdit(!edit);
            if (!edit) {
                // Entering edit mode - reset change tracking
                setHasChanges(false);
                setChangedFields(new Set());
            }
        }
    };

    const [updateSiteIncident, {loading: updateSiteIncidentLoading}] = useMutation(updateSiteIncidentMutation);

    const handleSaveChanges = async () => {
        const changedData = getChangedValues();

        await updateSiteIncident({
            variables: {
                input:{
                    _id: param?.incidentId,
                    metadata: changedData
                }
            }
        });

        // After successful save, update original values
        setOriginalValues(currentValues);
        setChangedFields(new Set());
        setHasChanges(false);
        setEdit(false);
        await refetch();

        dispatch(setNotification({
            error: false,
            notificationState: true,
            message: "Incident details updated successfully"
        }));
    };

    const [shareReport, setShareReport] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const { generatePDF, printPage } = usePDFGenerator();

    const handleGeneratePDF = async () => {
        setIsGeneratingPDF(true);
        try {
            const success = await generatePDF('print-section', `siteIncident-${siteIncident?._id || 'record'}`);
            if (!success) {
                alert('Failed to generate PDF. Please try again.');
            }
        } catch (error) {
            console.error('PDF generation error:', error);
            alert('Error generating PDF. Please try again.');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const menu = ()=>{
        return(
            <Menu>
                <Menu.Item icon={<EditOutlined/>} onClick={handleEditToggle}>
                    {edit? "Cancel Edit" : "Edit report"}
                </Menu.Item>

                {edit && hasChanges && (
                    <Menu.Item icon={<SaveOutlined/>} onClick={handleSaveChanges}>
                        Save Changes
                    </Menu.Item>
                )}

                <Menu.Item icon={<ShareAltOutlined/>} onClick={()=>setShareReport(!shareReport)}>
                    Share On Email
                </Menu.Item>

                <Menu.Item onClick={handleGeneratePDF} icon={<FilePdfOutlined />}>
                    Download Pdf {isGeneratingPDF? <Spinner width={5}/> :  null}
                </Menu.Item>

                <Menu.Item onClick={printPage} icon={<PrinterOutlined />}>
                    Print
                </Menu.Item>
            </Menu>
        )
    }

    const [shareComplaintReportEmail, {loading: shareComplaintReportEmailLoading}] = useMutation(shareComplaintReportEmailMutation);

    const sendIncidentOnEmail = async (email)=>{
        const pdfBlob = await generatePDF('print-section', `siteIncident-${siteIncident?._id || 'record'}`, true);
        const file = new File([pdfBlob], `siteIncident-${dayjs().format()}.pdf`, { type: 'application/pdf' });

        await shareComplaintReportEmail({
            variables:{
                    to: email,
                    pdfFile: file,
                    subject: "Site Incident",
                    message: `
This is an automated message from WFC 360.

Please find attached the PDF document containing the details of the reported incident.

No further action is required unless otherwise stated. If you have any questions, please contact our support team.

Thank you,
Intraguard Limited

Do not reply to this email – it is automatically generated.
                    `
            }
        });

        setShareReport(false);

        dispatch(setNotification({
            error: false,
            notificationState: true,
            message: "Site Incident has been shared over email"
        }))
    };

    // ── derived view state ──
    const m = currentValues || {};
    const metadataKeys = Object.keys(originalValues || {});

    // oldest first from the server; shown newest first, always ending with the
    // report itself (older incidents predate status rows and have none)
    const timeline = useMemo(() => {
        const rows = [...(siteIncident?.siteIncidentStatus || [])]
            .sort((a, b) => dayjs(a?.createdAt).valueOf() - dayjs(b?.createdAt).valueOf());
        if (siteIncident?._id && !rows.some((r) => r?.status === "new")) {
            rows.unshift({ _id: "reported", status: "new", comment: "", createdAt: siteIncident?.createdAt });
        }
        return rows.reverse();
    }, [siteIncident]);

    const latest = timeline[0]?.status || "new";
    const isClosed = timeline.some((t) => t?.status === "closed");
    const usedStatuses = timeline.map((t) => t?.status);
    const nextStatusOptions = statuses.filter((s) => !usedStatuses.includes(s.value));

    const reporter = [siteIncident?.employee?.firstname, siteIncident?.employee?.lastname].filter(Boolean).join(" ");
    const siteName = siteIncident?.site?.sitename;
    const siteAddress = [siteIncident?.site?.metadata?.address, siteIncident?.site?.metadata?.city, siteIncident?.site?.postcode].filter(Boolean).join(", ");
    const reference = siteIncident?._id ? `INC-${String(siteIncident._id).slice(-6).toUpperCase()}` : "";
    const reportedAt = siteIncident?.createdAt ? dayjs(siteIncident.createdAt).format("DD/MM/YYYY HH:mm") : "—";
    const occurred = formatOccurred(m.date, m.time);
    const policeAttended = String(m.policeAttended || "").trim().toLowerCase() === "yes";
    // "Police details: No" is just noise when the police never attended
    const showPoliceDetails = policeAttended || !(isBlankAnswer(m.policeDetails) || /^\s*no\.?\s*$/i.test(String(m.policeDetails)));

    // anything the officer's form wrote that none of the sections below cover
    const knownKeys = Object.keys(FIELDS);
    const otherKeys = metadataKeys.filter((k) => !knownKeys.includes(k));

    // `address` is never shown (so not editable either) - older app builds filled it from
    // the officer's first trained site rather than the one picked, so it can belong to
    // another site entirely; the Site card shows the incident site's own address instead
    const editKeys = [
        ...knownKeys.filter((k) => k !== "address" && metadataKeys.includes(k)),
        ...otherKeys,
    ];

    const changedStyle = (key) => changedFields.has(key) ? { borderColor: '#1890ff', boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)' } : undefined;

    const renderEditor = (key) => {
        const field = FIELDS[key] || { type: 'text' };
        const value = currentValues[key];
        if (field.type === 'date') {
            return (
                <DatePicker
                    value={value ? dayjs(value, "DD/MM/YYYY") : null}
                    onChange={(date) => handleFieldChange(key, date ? date.format("DD/MM/YYYY") : '')}
                    className="generic-input-control"
                    format={"DD/MM/YYYY"}
                    style={changedStyle(key)}
                />
            );
        }
        if (field.type === 'time') {
            return <Input type="time" value={value || ''} onChange={(e) => handleFieldChange(key, e.target.value)} className="generic-input-control" style={changedStyle(key)} />;
        }
        if (field.type === 'multiline') {
            return <TextArea value={value || ''} onChange={(e) => handleFieldChange(key, e.target.value)} className="generic-input-control" rows={4} style={changedStyle(key)} />;
        }
        if (field.type === 'dropdown') {
            return (
                <Select
                    value={value || undefined}
                    onChange={(v) => handleFieldChange(key, v)}
                    className="custom-select ir-select"
                    style={{ width: '100%' }}
                    options={field.options}
                    suffixIcon={<CaretDownFilled />}
                />
            );
        }
        return <Input value={value || ''} onChange={(e) => handleFieldChange(key, e.target.value)} className="generic-input-control" style={changedStyle(key)} />;
    };

    const scrollToComposer = () => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (loading && !siteIncident?._id) {
        return (
            <div className="ir-page">
                <div className="ir-header"><Skeleton active paragraph={{ rows: 3 }} /></div>
            </div>
        );
    }

    return (
        <div className="ir-page">
            <div id="print-section" className="ir-print-root">

                {/* ── Header ── */}
                <header className="ir-header">
                    <div className="ir-header-top">
                        <div className="ir-header-main">
                            <div className="ir-eyebrow">Incident report <span>{reference}</span></div>
                            <div className="ir-title-row">
                                <h1 className="ir-title">
                                    {m.incidentType || "Incident"} <span className="ir-title-at">at</span> {siteName || "—"}
                                </h1>
                                <StatusPill status={latest} />
                            </div>
                            <div className="ir-meta">
                                <span>Occurred <b>{occurred}</b></span>
                                <span>Reported <b>{reportedAt}</b>{reporter && <> by <b>{reporter}</b></>}</span>
                                {!isBlankAnswer(m.incidentOutcome) && <span>Outcome <b>{m.incidentOutcome}</b></span>}
                            </div>
                        </div>

                        <div className="ir-header-actions" data-html2canvas-ignore="true">
                            <Dropdown overlay={menu} trigger={['click']}>
                                <button type="button" className="ir-btn">Actions <CaretDownFilled /></button>
                            </Dropdown>
                            {!isClosed && (
                                <button type="button" className="ir-btn ir-btn-primary" onClick={scrollToComposer}>Update status</button>
                            )}
                        </div>
                    </div>

                </header>

                <div className="ir-body">

                    {/* ── Key facts ── */}
                    <aside className="ir-facts-col">

                        <Card title="Site">
                            <button type="button" className="ir-link ir-site-name" onClick={() => navigate(`/user/site-detail/${siteIncident?.siteId}`)}>
                                {siteName || "—"}
                            </button>
                            {siteAddress && <div className="ir-site-address">{siteAddress}</div>}
                        </Card>

                        <Card title="Details">
                            <dl className="ir-props">
                                <dt>Status</dt><dd><StatusPill status={latest} /></dd>
                                <dt>Reference</dt><dd>{reference}</dd>
                                <dt>Incident type</dt><dd><Text value={m.incidentType} empty="—" /></dd>
                                <dt>Outcome</dt><dd><Text value={m.incidentOutcome} empty="—" /></dd>
                                <dt>Occurred</dt><dd>{occurred}</dd>
                                <dt>Reported</dt><dd>{reportedAt}</dd>
                                <dt>Reported by</dt>
                                <dd>
                                    {reporter ? (
                                        <span className="ir-person">
                                            <span className="ir-avatar">{initials(reporter)}</span>
                                            <button type="button" className="ir-link" onClick={() => navigate(`/user/employee-detail/${siteIncident?.employeeId}`)}>{reporter}</button>
                                        </span>
                                    ) : "—"}
                                </dd>
                            </dl>
                        </Card>

                    </aside>

                    {/* ── Report ── */}
                    <div className="ir-main">
                        {edit ? (
                            <Card
                                title="Edit incident report"
                                className="ir-card-editing"
                                extra={<button type="button" className="ir-link" onClick={handleEditToggle}>Cancel</button>}
                            >
                                <div className="ir-edit-grid">
                                    {editKeys.map((key) => (
                                        <div key={key} className={FIELDS[key]?.type === 'multiline' ? "ir-edit-field ir-edit-field-full" : "ir-edit-field"}>
                                            <label className={changedFields.has(key) ? "ir-label ir-label-changed" : "ir-label"}>
                                                {humanizeKey(key)}{changedFields.has(key) && " *"}
                                            </label>
                                            {renderEditor(key)}
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        ) : (
                            <>
                                <Card title="Incident narrative"
                                    extra={<button type="button" className="ir-link" data-html2canvas-ignore="true" onClick={handleEditToggle}>Edit</button>}>
                                    {/* where the officer was / what they were doing when it happened -
                                        sets the scene, so it leads. Only on reports that have it: the
                                        current mobile form doesn't ask for it. */}
                                    {!isBlankAnswer(m.assignment) && (
                                        <>
                                            <div className="ir-label">Assignment</div>
                                            <p className="ir-narrative"><Text value={m.assignment} /></p>
                                        </>
                                    )}

                                    <div className="ir-label">What happened</div>
                                    <p className="ir-narrative"><Text value={m.incidentDetails} empty="No description was given." /></p>

                                    <div className="ir-label">Immediate action taken</div>
                                    <ImmediateAction value={m.immediateAction} />
                                </Card>

                                <Card title="Police & evidence">
                                    <div className="ir-facts">
                                        <div>
                                            <div className="ir-label">Police attended</div>
                                            <YesNo value={m.policeAttended} />
                                        </div>
                                        <div>
                                            <div className="ir-label">CCTV footage</div>
                                            <YesNo value={m.cctv} />
                                        </div>
                                        {showPoliceDetails && (
                                            <div className="ir-facts-wide">
                                                <div className="ir-label">Police details</div>
                                                <div className="ir-value"><Text value={m.policeDetails} /></div>
                                            </div>
                                        )}
                                    </div>
                                </Card>

                                <Card title="People involved">
                                    <div className="ir-facts ir-facts-2">
                                        <div>
                                            <div className="ir-label">Others involved</div>
                                            <div className="ir-value"><Text value={m.othersInvolved} /></div>
                                        </div>
                                        <div>
                                            <div className="ir-label">Witnesses</div>
                                            <div className="ir-value"><Text value={m.witnesses} /></div>
                                        </div>
                                    </div>
                                    <div className="ir-declaration">
                                        <div className="ir-label">Officer declaration</div>
                                        <div className="ir-value">
                                            Signed by <b><Text value={m.signature} empty="—" /></b>
                                            <span className="ir-muted"> · submitted {reportedAt}</span>
                                        </div>
                                    </div>
                                </Card>

                                {otherKeys.length > 0 && (
                                    <Card title="Other details">
                                        <div className="ir-facts ir-facts-2">
                                            {otherKeys.map((key) => (
                                                <div key={key}>
                                                    <div className="ir-label">{humanizeKey(key)}</div>
                                                    <div className="ir-value"><Text value={m[key]} /></div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                )}
                            </>
                        )}
                    </div>

                    {/* ── Timeline ── */}
                    <aside className="ir-side">
                        <Card title="Incident timeline">
                            <IncidentTimeline timeline={timeline} reporter={reporter} />

                            {!isClosed && (
                                <div className="ir-composer" ref={composerRef} data-html2canvas-ignore="true">
                                    <div className="ir-label">Update status</div>
                                    <Select
                                        placeholder="Change status to…"
                                        className="custom-select ir-select"
                                        style={{ width: '100%' }}
                                        value={currentStatus}
                                        onChange={setCurrentStatus}
                                        suffixIcon={<CaretDownFilled />}
                                        options={nextStatusOptions.map((s) => ({ value: s.value, label: s.label }))}
                                    />
                                    <TextArea
                                        className="generic-input-control"
                                        placeholder="Add a note - what was done, who was contacted, next steps…"
                                        rows={3}
                                        value={comment}
                                        onChange={(e) => setComment(e?.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="ir-btn ir-btn-primary ir-btn-block"
                                        onClick={handelStatusUpdate}
                                        disabled={updateSiteIncidentStatusLoading || !currentStatus}
                                    >
                                        {updateSiteIncidentStatusLoading ? <Spinner /> : "Post update"}
                                    </button>
                                </div>
                            )}
                        </Card>
                    </aside>
                </div>
            </div>

            {
                shareReport?
                <ReportShare
                    visible={shareReport}
                    close={()=>setShareReport(false)}
                    sendReportOnEmail={sendIncidentOnEmail}
                    loading={shareComplaintReportEmailLoading}
                />
                : null
            }

            {
                    hasChanges?
                        <div className='hr-action-footer' style={{left:0 }}>
                                <button disabled={updateSiteIncidentLoading} className={updateSiteIncidentLoading? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'} onClick={()=>handleSaveChanges()}>
                                    {updateSiteIncidentLoading? <Spinner/> : "Save"}
                                </button>

                                <button disabled={updateSiteIncidentLoading} className={updateSiteIncidentLoading? 'disabled-btn drawer-outlined-btn' : 'drawer-outlined-btn'} onClick={()=>{setHasChanges(false); resetChanges()}}>
                                    {updateSiteIncidentLoading? <Spinner/> : "Cancel"}
                                </button>

                                <span className='text' style={{margin: 0}}>
                                    You've changed {changedFields.size} data field{changedFields.size > 1 ? 's' : ''}
                                </span>
                        </div>
                    :null
            }
        </div>
    );
}
